<script setup>
// §8.2 Modal — elevated. Used for destructive confirm (§8.2).
// Built on the NATIVE <dialog> element: showModal() gives a focus trap, Esc to
// close, `inert` background and the ::backdrop overlay for free — no JS library,
// no focus-management code. We only sync the `open` model with the element.
import { computed, ref, watch, onMounted } from 'vue'
import LIcon from '../base/Icon.vue'

const props = defineProps({
  title: { type: String, required: true },
  description: { type: String, default: null },
  // When true, the only ways to close are the X button or a footer action —
  // a backdrop click and Esc are both ignored. Use for forms where an
  // accidental dismissal would silently lose typed input.
  persistent: { type: Boolean, default: false },
  // Override the default 460px panel width for content that genuinely needs
  // the room (a list, a table). Same contract as LDrawer's `width`.
  width: { type: [String, Number], default: null },
})
const open = defineModel('open', { default: false })
const dlg = ref(null)
const modalStyle = computed(() => {
  if (props.width === null || props.width === undefined || props.width === '') return null
  return { width: typeof props.width === 'number' ? `${props.width}px` : props.width }
})

function sync(v) {
  const el = dlg.value
  if (!el) return
  if (v && !el.open) el.showModal()
  else if (!v && el.open) el.close()
}
watch(open, sync)
onMounted(() => sync(open.value))

// Native close (Esc, or the form method="dialog" buttons) → keep the model honest.
function onClose() {
  open.value = false
}
// Esc fires `cancel` before `close` — block it here for a persistent modal so
// onClose() never runs.
function onCancel(e) {
  if (props.persistent) e.preventDefault()
}
// Click on the backdrop (the <dialog> itself, outside the inner panel) closes it,
// unless the modal is persistent.
function onClick(e) {
  if (!props.persistent && e.target === dlg.value) open.value = false
}
</script>

<template>
  <dialog ref="dlg" class="l-modal" :style="modalStyle" @close="onClose" @cancel="onCancel" @click="onClick">
    <div class="inner">
      <div class="mh">
        <h2 class="mt">{{ title }}</h2>
        <button class="mx" type="button" aria-label="Close" @click="open = false">
          <LIcon name="xmark" :size="16" />
        </button>
      </div>
      <p v-if="description" class="md">{{ description }}</p>
      <div class="mb"><slot /></div>
      <div v-if="$slots.footer" class="mf"><slot name="footer" /></div>
    </div>
  </dialog>
</template>

<style scoped>
.l-modal {
  /* restore the dialog's default centring, which the base reset (* { margin:0 })
     would otherwise strip */
  margin: auto;
  width: 460px;
  max-width: calc(100vw - 32px);
  padding: 0;
  border: 0;
  border-radius: var(--r-lg);
  background: var(--surface);
  color: var(--text);
  box-shadow: var(--shadow);
}
.l-modal::backdrop {
  background: var(--overlay);
}
/* progressive-enhancement fade-in; instant + robust where @starting-style is
   unsupported, and disabled under prefers-reduced-motion (base.css). */
.l-modal[open] {
  animation: modal-in var(--t-fast) var(--ease);
}
@keyframes modal-in {
  from {
    opacity: 0;
    transform: translateY(6px);
  }
}
.inner {
  padding: var(--sp-6);
  display: flex;
  flex-direction: column;
  gap: var(--sp-4);
}
.mh {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  gap: var(--sp-3);
}
.mt {
  font-family: var(--font-display);
  font-size: var(--text-card);
  font-weight: 600;
}
.mx {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: var(--target-min);
  height: var(--target-min);
  border-radius: var(--r-sm);
  color: var(--text-2);
}
.mx:hover {
  background: var(--surface-sunken);
  color: var(--text);
}
.md {
  font-size: var(--text-base);
  color: var(--text-2);
  line-height: 1.55;
  margin-top: calc(-1 * var(--sp-2));
}
/* Wraps rather than overflowing: a right-aligned row that doesn't fit spills off the LEFT edge,
   where it can't be scrolled to, so the first action becomes unreachable in a narrow window. */
.mf {
  display: flex;
  flex-wrap: wrap;
  justify-content: flex-end;
  gap: var(--sp-2);
}
</style>
